import { WORKFLOW_RUN_INTRO_ASK_ID_PREFIX } from '@digital-twins/shared-types'

import { historicStudyPinAfterIndex } from './userTurnCommandChip'
import { WORKFLOW_RUN_ASK_TURN_ASK_ID_PREFIX } from './workflowRunAskTurnAskId'
import { WORKFLOW_RUN_COMPLETION_ASK_ID_PREFIX } from './workflowRunCompletion'

/**
 * Declared once in shared-types (`workflow-run-ask-ids.ts`) and re-exported
 * here for this page's importers. A run that minted its own thread persists
 * an intro line under this id; a run started inside an existing thread has
 * only the ask turn.
 */
export { WORKFLOW_RUN_INTRO_ASK_ID_PREFIX }

interface AnchorTurn {
  id: string
  role: string
  askId?: string | null
}

export interface StudyPinAfterIndexOptions {
  isLive?: boolean
}

/**
 * Index of the turn the study pin should sit directly under, or null to leave
 * it at the end of the current exchange.
 *
 * The pin used to hang off the ASK that started the run. That reads correctly
 * only until the reader continues the thread: the results then belong to a
 * question several turns up, and the pin either follows an ask that no longer
 * looks like the reason for it, or is dragged into the current exchange next
 * to an unrelated question. Anchoring on the run's own completion
 * announcement instead puts the output where the announcement says it is, and
 * it stays there however far the thread runs on (Step 1c-i).
 *
 * Two anchors, in order:
 *
 * 1. **The announcement** for this exact run token. The intended anchor.
 * 2. **The originating ask**, the previous behavior, kept as the fallback for
 *    a run that finished before the backend persisted announcements (81ca08901)
 *    and therefore has none. Without this a completed older thread would lose
 *    its pin position entirely.
 *
 * A still-open run (`options.isLive`) uses neither: the announcement does not
 * exist yet, and the historic fallback would park Continue under the
 * originating ask after a follow-up, burying the decision above the current
 * exchange. It anchors on the run's OWN opening turn instead — the later of
 * the intro line (a run that minted the thread) and the ask turn (a run
 * started inside one) — so a message typed while the run is in flight reads
 * below the cards it is about instead of pushing all of them under itself
 * (D6). Null, today's behavior, when the run has neither turn.
 */
export function studyPinAfterIndex(
  turns: Array<AnchorTurn>,
  runToken: string | null | undefined,
  originatingAskId: string | null,
  exchangeStart: number,
  options?: StudyPinAfterIndexOptions,
): number | null {
  if (options?.isLive) {
    return liveRunPinAfterIndex(turns, runToken)
  }

  if (runToken) {
    const announcementAskId = `${WORKFLOW_RUN_COMPLETION_ASK_ID_PREFIX}${runToken}`
    const announcedAt = turns.findIndex(
      (turn) => turn.askId === announcementAskId,
    )
    if (announcedAt >= 0) {
      /* The announcement is the anchor wherever it sits, current exchange
         included: a follow-up typed after it must not pull the results down
         past the question that has nothing to do with them. */
      return announcedAt
    }
  }
  return historicStudyPinAfterIndex(turns, originatingAskId, exchangeStart)
}

/**
 * Index of this run's own opening turn while it is still in flight: the LATER
 * of `workflow-run-intro:<token>` and `workflow-run-ask:<token>`. Both can be
 * present on a run that minted its thread, and the ask is written second.
 * Null when the run persisted neither, which leaves the pin at the end of the
 * current exchange exactly as before.
 */
function liveRunPinAfterIndex(
  turns: Array<AnchorTurn>,
  runToken: string | null | undefined,
): number | null {
  if (!runToken) {
    return null
  }

  const introAskId = `${WORKFLOW_RUN_INTRO_ASK_ID_PREFIX}${runToken}`
  const askAskId = `${WORKFLOW_RUN_ASK_TURN_ASK_ID_PREFIX}${runToken}`

  for (let i = turns.length - 1; i >= 0; i -= 1) {
    const askId = turns[i].askId
    if (askId === introAskId || askId === askAskId) {
      return i
    }
  }

  return null
}
